import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    CircularProgress,
    Modal,
    Backdrop,
    Fade,
    Autocomplete
} from '@mui/material';
import axios from '../axiosConfig';
import Sidebar from './Sidebar';

const ConversionPage = () => {
    // Состояния для отображения формы и списка
    const [showCreateConversionForm, setShowCreateConversionForm] = useState(false);

    // Состояния для создания конверсии
    const [isFiat, setIsFiat] = useState(false);
    const [baseAsset, setBaseAsset] = useState(null);
    const [quoteAsset, setQuoteAsset] = useState(null);
    const [amount, setAmount] = useState('');
    const [side, setSide] = useState('');
    const [basePrice, setBasePrice] = useState('');

    // Состояние для активов
    const [assets, setAssets] = useState([]);

    // Состояния для списка конверсий
    const [conversions, setConversions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalConversions, setTotalConversions] = useState(0);

    // Состояние для подтверждения и статуса конверсии
    const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
    const [currentSk, setCurrentSk] = useState('');

    // Стороны сделки
    const sides = ['buy', 'sell'];

    // Получение списка активов в зависимости от isFiat
    const fetchAssets = (isFiat) => {
        const endpoint = isFiat ? '/transaction-info/fiat/assets' : '/transaction-info/crypto/assets';
        axios.get(endpoint)
            .then((response) => {
                setAssets(response.data || []);
            })
            .catch((error) => {
                console.error('Ошибка при получении списка активов:', error);
            });
    };

    // Функция для получения списка конверсий
    const fetchConversionsData = (pageNumber, pageSize) => {
        setLoading(true);
        axios.get('/transaction-info/conversion/list', {
            params: { page_number: pageNumber + 1, page_size: pageSize }
        })
            .then((response) => {
                const data = response.data.conversion_list || [];
                setConversions(Array.isArray(data) ? data : []);
                setTotalConversions(response.data.page_amount * pageSize);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Ошибка при получении списка конверсий:', error);
                setLoading(false);
            });
    };

    // Функция для создания конверсии
    const handleCreateConversion = () => {
        const conversionData = {
            base_asset: baseAsset,
            quote_asset: quoteAsset,
            amount: Number(amount),
            side: side,
            base_price: Number(basePrice),
            is_fiat: isFiat,
        };

        axios.post('/transaction/conversion/create', conversionData)
            .then(response => {
                alert('Конвертация успешно создана');
                setCurrentSk(response.data.sk);  // Сохраняем sk для последующего обновления статуса
                setOpenConfirmationModal(true);  // Открываем модальное окно для подтверждения
                setShowCreateConversionForm(false);  // Скрываем форму после создания
                fetchConversionsData(page, rowsPerPage);  // Обновляем список
            })
            .catch(error => {
                console.error('Ошибка при создании конверсии:', error);
                alert('Ошибка при создании конверсии');
            });
    };

    // Функция для обновления статуса конверсии на SUCCESS
    const handleConfirmSuccess = () => {
        const updateData = {
            sk: currentSk,
            status: 'SUCCESS'
        };

        axios.post('/transaction/conversion/update', updateData)
            .then(response => {
                alert('Конвертация успешно обновлена до статуса SUCCESS');
                setOpenConfirmationModal(false);  // Закрываем модальное окно
                fetchConversionsData(page, rowsPerPage);  // Обновляем список после обновления
            })
            .catch(error => {
                console.error('Ошибка при обновлении конверсии:', error);
                alert('Ошибка при обновлении конверсии');
            });
    };

    // Пагинация для списка
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        fetchConversionsData(newPage, rowsPerPage);
    };

    // Обработка изменения количества строк на странице
    const handleRowsPerPageChange = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        fetchConversionsData(0, newRowsPerPage);
    };

    // Загрузка списка при первом рендере
    useEffect(() => {
        fetchConversionsData(page, rowsPerPage);
    }, [page, rowsPerPage]);

    // Загрузка активов при изменении isFiat
    useEffect(() => {
        fetchAssets(isFiat);
    }, [isFiat]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
                <Sidebar /> {/* Отображение Sidebar */}
            </Grid>
            <Grid item xs={12} md={8}>
                <Box mt={2}>
                    <Typography variant="h4" gutterBottom>
                        {showCreateConversionForm ? "Создание Конвертации" : "Список Конвертаций"}
                    </Typography>

                    {/* Кнопка для создания новой конверсии */}
                    {!showCreateConversionForm && (
                        <Box mb={2}>
                            <Button variant="contained" color="primary" onClick={() => setShowCreateConversionForm(true)}>
                                Создать Конвертацию
                            </Button>
                        </Box>
                    )}

                    {/* Форма для создания конверсии */}
                    {showCreateConversionForm && (
                        <Box>
                            <FormControlLabel
                                control={<Switch checked={isFiat} onChange={(e) => setIsFiat(e.target.checked)} />}
                                label="Фиат"
                            />

                            <Autocomplete
                                options={assets}
                                getOptionLabel={(option) => option}
                                value={baseAsset}
                                onChange={(event, newValue) => setBaseAsset(newValue)}
                                renderInput={(params) => <TextField {...params} label="Базовый актив" margin="normal" fullWidth />}
                            />

                            <Autocomplete
                                options={assets}
                                getOptionLabel={(option) => option}
                                value={quoteAsset}
                                onChange={(event, newValue) => setQuoteAsset(newValue)}
                                renderInput={(params) => <TextField {...params} label="Котируемый актив" margin="normal" fullWidth />}
                            />

                            <TextField
                                label="Сумма *"
                                variant="outlined"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                type="number"
                                fullWidth
                                margin="normal"
                            />

                            <Autocomplete
                                options={sides}
                                getOptionLabel={(option) => option}
                                value={side}
                                onChange={(event, newValue) => setSide(newValue)}
                                renderInput={(params) => <TextField {...params} label="Сторона сделки" margin="normal" fullWidth />}
                            />

                            <TextField
                                label="Базовая цена *"
                                variant="outlined"
                                value={basePrice}
                                onChange={(e) => setBasePrice(e.target.value)}
                                type="number"
                                fullWidth
                                margin="normal"
                            />

                            <Box mt={2}>
                                <Button variant="contained" color="primary" onClick={handleCreateConversion}>
                                    Создать Конвертацию
                                </Button>
                                <Button variant="contained" color="secondary" onClick={() => setShowCreateConversionForm(false)} style={{ marginLeft: '10px' }}>
                                    Отмена
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Таблица с конверсиями */}
                    {!showCreateConversionForm && (
                        <Box mt={4}>
                            {loading ? (
                                <CircularProgress />
                            ) : (
                                <Paper>
                                    <div style={{ overflowX: 'auto' }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Базовый актив</TableCell>
                                                    <TableCell>Котируемый актив</TableCell>
                                                    <TableCell>Сумма</TableCell>
                                                    <TableCell>Сторона</TableCell>
                                                    <TableCell>Статус</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {conversions.length > 0 ? (
                                                    conversions.map((conversion, index) => (
                                                        <TableRow key={conversion.pk || index}>
                                                            <TableCell>{conversion.base_asset}</TableCell>
                                                            <TableCell>{conversion.quote_asset}</TableCell>
                                                            <TableCell>{conversion.amount}</TableCell>
                                                            <TableCell>{conversion.side}</TableCell>
                                                            <TableCell>{conversion.status}</TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={6}>Нет данных для отображения</TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                    <TablePagination
                                        component="div"
                                        count={totalConversions}
                                        page={page}
                                        onPageChange={handlePageChange}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={handleRowsPerPageChange}
                                        labelRowsPerPage="Строк на странице:"
                                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                                    />
                                </Paper>
                            )}
                        </Box>
                    )}

                    {/* Модальное окно для подтверждения обновления статуса */}
                    <Modal
                        open={openConfirmationModal}
                        onClose={() => setOpenConfirmationModal(false)}
                        closeAfterTransition
                        BackdropComponent={Backdrop}
                        BackdropProps={{
                            timeout: 500,
                        }}
                    >
                        <Fade in={openConfirmationModal}>
                            <Box
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: 400,
                                    backgroundColor: 'white',
                                    padding: '16px',
                                    borderRadius: '4px',
                                    boxShadow: '0px 3px 6px rgba(0,0,0,0.2)',
                                }}
                            >
                                <Typography variant="h6" gutterBottom>
                                    Подтвердите обновление статуса
                                </Typography>
                                <Typography variant="body1">
                                    Вы уверены, что хотите обновить статус конверсии до "SUCCESS"?
                                </Typography>
                                <Box mt={3}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleConfirmSuccess}
                                        style={{ marginRight: '10px' }}
                                    >
                                        Подтвердить
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => setOpenConfirmationModal(false)}
                                    >
                                        Отмена
                                    </Button>
                                </Box>
                            </Box>
                        </Fade>
                    </Modal>
                </Box>
            </Grid>
        </Grid>
    );
};

export default ConversionPage;
